import { useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const iso = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}-01`;

/** Month-only picker: 12 rolling months from the current month; ◀ ▶ move a full 12 months; the year sits above every month (blue where it starts / at January).
 *  value / onChange use "YYYY-MM-01". children = the cell text that opens it. */
export default function MonthPicker({ value, onChange, testid, disabled, children, iconOnly = false }) {
  const [open, setOpen] = useState(false);
  const now = new Date();
  const [offset, setOffset] = useState(0);
  const start = now.getFullYear() * 12 + now.getMonth() + offset * 12;
  const months = Array.from({ length: 12 }, (_, i) => ({ y: Math.floor((start + i) / 12), m: (start + i) % 12 }));
  const sel = (value || "").slice(0, 7);
  if (disabled) return children;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setOffset(0); }}>
      <PopoverTrigger asChild>
        <button type="button" className="group/mp relative inline-flex items-center rounded px-1 py-0.5 -mx-1 text-left hover:bg-blue-50/50 hover:text-[#007BFF]" data-testid={testid}>
          {iconOnly ? <CalendarDays className="h-3.5 w-3.5 text-slate-400 group-hover/mp:text-[#007BFF]" /> : (<>
            {children}
            <CalendarDays className="absolute -right-3 h-3 w-3 shrink-0 text-slate-400 opacity-0 transition-opacity group-hover/mp:opacity-100" />
          </>)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[264px] p-2" data-testid={`${testid}-picker`}>
        <div className="mb-1.5 flex items-center justify-between">
          <button type="button" onClick={() => setOffset((o) => o - 1)} className="rounded p-1 text-slate-500 hover:bg-slate-100" data-testid={`${testid}-prev`}><ChevronLeft className="h-3.5 w-3.5" /></button>
          <span className="text-[11px] font-semibold text-muted-foreground" data-testid={`${testid}-range`}>
            {MONTHS[months[0].m]} {months[0].y} – {MONTHS[months[11].m]} {months[11].y}
          </span>
          <button type="button" onClick={() => setOffset((o) => o + 1)} className="rounded p-1 text-slate-500 hover:bg-slate-100" data-testid={`${testid}-next`}><ChevronRight className="h-3.5 w-3.5" /></button>
        </div>
        <div className="grid grid-cols-4 gap-1">
          {months.map(({ y, m }, i) => {
            const on = sel === iso(y, m).slice(0, 7);
            return (
              <button key={`${y}-${m}`} type="button" onClick={() => { setOpen(false); onChange(iso(y, m)); }}
                      className={cn("flex flex-col items-center rounded py-1 text-xs leading-tight transition-colors", on ? "bg-[#007BFF] text-white" : "text-slate-700 hover:bg-slate-100")}
                      data-testid={`${testid}-month-${y}-${m + 1}`}>
                <span className={cn("h-3 text-[9px] font-semibold", on ? "text-white/80" : y % 2 === 0 ? "text-[#007BFF]" : "text-red-800 dark:text-red-400")} data-testid={(m === 0 || i === 0) ? `${testid}-year-${y}` : undefined}>
                  {y}
                </span>
                {MONTHS[m]}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
